
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Calendar, Shield, Lightbulb, TrendingUp } from "lucide-react";

export const PatentDocumentation = () => {
  const [activeSection, setActiveSection] = useState("overview");

  const patentableElements = [
    {
      id: "hybrid-ai-search",
      title: "Hybrid AI-Enhanced Search Architecture",
      description: "Combinatie van AI parsing, database queries en intelligente fallback mechanismen",
      technicalDetails: [
        "AI-gestuurde query parsing met GPT-4 integration",
        "Multi-layer search strategie (AI → Database → Fallback)",
        "Real-time search criteria optimization",
        "Adaptive result ranking algoritme"
      ],
      novelty: "Unieke combinatie van AI parsing met structured database queries in travel domain",
      files: ["ai-vacation-search/index.ts", "searchExecutor.ts", "aiParser.ts"]
    },
    {
      id: "self-learning-optimization",
      title: "Self-Learning Query Optimization System",
      description: "Automatisch lerende zoekoptimalisatie met wekelijkse AI-analyse",
      technicalDetails: [
        "Automated search performance analysis via cron jobs",
        "AI-driven query improvement suggestions",
        "Dynamic search criteria enhancement",
        "Machine learning feedback loop for search quality"
      ],
      novelty: "Volledig geautomatiseerd zelflerende systeem voor travel search optimization",
      files: ["analyze-search-quality/index.ts", "update-search-performance/index.ts"]
    },
    {
      id: "adaptive-search-strategy",
      title: "Adaptive Search Strategy Engine",
      description: "Dynamic switching tussen verschillende zoekmethoden gebaseerd op query type",
      technicalDetails: [
        "Intelligent query classification (location/type/keyword)",
        "Dynamic search path selection algoritme",
        "Contextual fallback mechanism hierarchy",
        "Real-time search strategy optimization"
      ],
      novelty: "Context-aware search strategy selection in travel booking systems",
      files: ["searchExecutor.ts", "queryBuilder.ts"]
    },
    {
      id: "realtime-analytics",
      title: "Real-time Analytics Integration",
      description: "Live tracking en directe zoekprestatie verbetering",
      technicalDetails: [
        "Real-time user interaction tracking",
        "Dynamic click-through rate optimization",
        "Live search performance metrics",
        "Automated query improvement deployment"
      ],
      novelty: "Realtime feedback systeem dat direct zoekresultaten verbetert",
      files: ["searchTracking.ts", "search_analytics table", "search_performance table"]
    }
  ];

  const generateDocumentation = () => {
    const timestamp = new Date().toISOString();
    const documentation = {
      timestamp,
      title: "AI Vacation Search - Patent Documentation",
      inventor: "Vakantie Zoekplatform",
      patentableElements,
      technicalArchitecture: {
        frontend: "React + TypeScript + Tailwind CSS",
        backend: "Supabase Edge Functions + Deno",
        ai: "OpenAI GPT-4 API integration",
        database: "PostgreSQL with analytics tables",
        realtime: "Supabase realtime subscriptions"
      },
      uniqueFeatures: [
        "Hybrid AI-database search approach",
        "Self-learning query optimization",
        "Adaptive search strategy selection",
        "Real-time performance analytics"
      ]
    };

    const blob = new Blob([JSON.stringify(documentation, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patent-documentation-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sections = [
    { id: "overview", title: "Overzicht", icon: FileText },
    { id: "elements", title: "Patenteerbare Elementen", icon: Lightbulb },
    { id: "timeline", title: "Timeline & Planning", icon: Calendar },
    { id: "strategy", title: "Patent Strategie", icon: Shield }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">
          Patent Documentatie - AI Vacation Search
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Technische documentatie voor patent aanvraag van de innovatieve AI-gestuurde zoekfunctie
        </p>
        <div className="flex justify-center gap-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Fase 1: Documentatie
          </Badge>
          <Badge variant="outline">
            Gegenereerd: {new Date().toLocaleDateString('nl-NL')}
          </Badge>
        </div>
      </div>

      <div className="flex gap-2 justify-center flex-wrap">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <Button
              key={section.id}
              variant={activeSection === section.id ? "default" : "outline"}
              onClick={() => setActiveSection(section.id)}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              {section.title}
            </Button>
          );
        })}
      </div>

      {activeSection === "overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Innovatie Samenvatting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                De AI Vacation Search implementeert een unieke hybride benadering die AI-parsing 
                combineert met gestructureerde database queries en zelflerende optimalisatie.
              </p>
              <div className="space-y-2">
                <h4 className="font-semibold">Kernvernieuwingen:</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Hybride AI-database zoekarchitectuur</li>
                  <li>• Automatisch lerende query optimalisatie</li>
                  <li>• Adaptieve zoekstrategie selectie</li>
                  <li>• Real-time prestatie analytics</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Patent Potentieel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">4</div>
                  <div className="text-sm text-green-700">Patenteerbare Elementen</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">85%</div>
                  <div className="text-sm text-blue-700">Noveltiy Score</div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Sterke patent positie door unieke combinatie van bestaande technieken 
                in een nieuwe architectuur voor de travel industry.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {activeSection === "elements" && (
        <div className="space-y-6">
          {patentableElements.map((element, index) => (
            <Card key={element.id} className="border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {index + 1}. {element.title}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {element.description}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    Patent Claim {index + 1}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Technische Details:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {element.technicalDetails.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-1">Novelty Aspect:</h4>
                  <p className="text-sm text-yellow-700">{element.novelty}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Relevante Code Files:</h4>
                  <div className="flex flex-wrap gap-2">
                    {element.files.map((file, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {file}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeSection === "timeline" && (
        <Card>
          <CardHeader>
            <CardTitle>Patent Evaluatie Timeline</CardTitle>
            <CardDescription>
              Stapsgewijze aanpak voor patent aanvraag over de komende 3 maanden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {[
                {
                  phase: "Week 1-2",
                  title: "Documentatie & Prior Art Research",
                  cost: "€1.500",
                  tasks: [
                    "Complete technische specificaties vastleggen",
                    "Proof of concept dokumenteren met timestamps",
                    "Prior art search in patent databases",
                    "Concurrent analysis (TUI, Booking.com)"
                  ],
                  status: "current"
                },
                {
                  phase: "Week 3-4",
                  title: "Patent Attorney Consultatie",
                  cost: "€1.000",
                  tasks: [
                    "Professional patentability assessment",
                    "Prior art validation door expert",
                    "Patent strategie ontwikkeling",
                    "Go/No-go beslissing"
                  ],
                  status: "upcoming"
                },
                {
                  phase: "Maand 2",
                  title: "Provisional Patent Filing",
                  cost: "€3.000",
                  tasks: [
                    "Vroege priority datum claimen",
                    "Provisional patent application indienen",
                    "12 maanden bescherming verkrijgen",
                    "Markt testen met patent pending status"
                  ],
                  status: "future"
                },
                {
                  phase: "Maand 3-12",
                  title: "Volledige Patent Aanvraag",
                  cost: "€25.000",
                  tasks: [
                    "Complete patent specificatie schrijven",
                    "EU patent aanvraag indienen",
                    "USPTO filing voor VS bescherming",
                    "Patent prosecution proces"
                  ],
                  status: "future"
                }
              ].map((phase, index) => (
                <div key={index} className="flex gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    phase.status === 'current' ? 'bg-blue-500 text-white' :
                    phase.status === 'upcoming' ? 'bg-yellow-500 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{phase.phase}: {phase.title}</h3>
                      <Badge variant="outline">{phase.cost}</Badge>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {phase.tasks.map((task, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="text-gray-400">•</span>
                          {task}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeSection === "strategy" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Potentiële Voordelen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <h4 className="font-semibold">Financiële Voordelen:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Licentie-inkomsten: €50k-150k/jaar</li>
                  <li>• Verhoogde bedrijfswaarde: +25-40%</li>
                  <li>• Exclusieve marktpositie 20 jaar</li>
                  <li>• Subsidie mogelijkheden (WBSO, etc.)</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Strategische Voordelen:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Bescherming tegen concurrentie</li>
                  <li>• Onderhandelingspositie versterking</li>
                  <li>• Technology leadership in travel AI</li>
                  <li>• White-labeling opportunities</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-orange-700">Risico's & Overwegingen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <h4 className="font-semibold">Financiële Risico's:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Investering: €35k zonder garantie</li>
                  <li>• Jaarlijkse onderhoud kosten</li>
                  <li>• Mogelijke patent disputes</li>
                  <li>• Lange ROI periode (3-5 jaar)</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Technische Risico's:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Prior art kan patentability blokkeren</li>
                  <li>• Snelle technologie ontwikkeling</li>
                  <li>• Bestaande patents van grote spelers</li>
                  <li>• Software patent uitdagingen</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex justify-center gap-4 pt-6 border-t">
        <Button onClick={generateDocumentation} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Download Documentatie
        </Button>
        <Button variant="outline" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Export naar PDF
        </Button>
      </div>
    </div>
  );
};
